import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Link, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { QueryProvider } from "@/providers/QueryProvider";
import { GuestRoute, ProtectedRoute, AuthGuard, AuthProvider } from "@/features/auth";
import { ErrorBoundary } from "@/components/error/ErrorBoundary";
import { AppLayout, PublicAppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { LandingPage } from "@/features/landing";

// Lazy load auth pages
const LoginPage = lazy(() =>
  import("@/features/auth/pages/LoginPage").then((m) => ({ default: m.LoginPage }))
);
const EnterpriseSignupPage = lazy(() =>
  import("@/features/auth/pages/EnterpriseSignupPage").then((m) => ({
    default: m.EnterpriseSignupPage,
  }))
);
const ForgotPasswordPage = lazy(() =>
  import("@/features/auth/pages/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  }))
);
const OAuthCallbackPage = lazy(() =>
  import("@/features/auth/pages/OAuthCallbackPage").then((m) => ({
    default: m.OAuthCallbackPage,
  }))
);
const PendingApprovalPage = lazy(() =>
  import("@/features/auth/pages/PendingApprovalPage").then((m) => ({
    default: m.PendingApprovalPage,
  }))
);
const ResetPasswordPage = lazy(() =>
  import("@/features/auth/pages/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  }))
);

// Lazy load profile pages
const ProfilePage = lazy(() =>
  import("@/features/profile/pages/ProfilePage").then((m) => ({
    default: m.ProfilePage,
  }))
);
const DashboardPage = lazy(() =>
  import("@/features/profile/pages/DashboardPage").then((m) => ({
    default: m.DashboardPage,
  }))
);

// Lazy load talent search pages (Enterprise/HR)
const TalentSearchPage = lazy(() =>
  import("@/features/profile/pages/TalentSearchPage").then((m) => ({
    default: m.TalentSearchPage,
  }))
);
const TalentProfilePage = lazy(() =>
  import("@/features/profile/pages/TalentProfilePage").then((m) => ({
    default: m.TalentProfilePage,
  }))
);

// Lazy load enterprise profile page
const EnterpriseProfilePage = lazy(() =>
  import("@/features/profile/pages/EnterpriseProfilePage").then((m) => ({
    default: m.EnterpriseProfilePage,
  }))
);

// Lazy load admin profile page
const AdminProfilePage = lazy(() =>
  import("@/features/profile/pages/AdminProfilePage").then((m) => ({
    default: m.AdminProfilePage,
  }))
);

// Lazy load HR profile page
const HRProfilePage = lazy(() =>
  import("@/features/profile/pages/HRProfilePage").then((m) => ({
    default: m.HRProfilePage,
  }))
);

// Lazy load opening pages
const OpeningsListPage = lazy(() =>
  import("@/features/openings/pages/student/OpeningsListPage").then((m) => ({
    default: m.OpeningsListPage,
  }))
);
const OpeningDetailPage = lazy(() =>
  import("@/features/openings/pages/student/OpeningDetailPage").then((m) => ({
    default: m.OpeningDetailPage,
  }))
);
const EnterpriseJobsPage = lazy(() =>
  import("@/features/openings/pages/enterprise/EnterpriseJobsPage").then((m) => ({
    default: m.EnterpriseJobsPage,
  }))
);
const CreateJobPage = lazy(() =>
  import("@/features/openings/pages/enterprise/CreateJobPage").then((m) => ({
    default: m.CreateJobPage,
  }))
);
const EnterpriseJobDetailPage = lazy(() =>
  import("@/features/openings/pages/enterprise/EnterpriseJobDetailPage").then((m) => ({
    default: m.EnterpriseJobDetailPage,
  }))
);
const EditJobPage = lazy(() =>
  import("@/features/openings/pages/enterprise/EditJobPage").then((m) => ({
    default: m.EditJobPage,
  }))
);
const HRJobReviewPage = lazy(() =>
  import("@/features/openings/pages/hr/HRJobReviewPage").then((m) => ({
    default: m.HRJobReviewPage,
  }))
);
const HRJobDetailPage = lazy(() =>
  import("@/features/openings/pages/hr/HRJobDetailPage").then((m) => ({
    default: m.HRJobDetailPage,
  }))
);

// Lazy load application pages - Student
const MyApplicationsPage = lazy(() =>
  import("@/features/applications/pages/student/MyApplicationsPage").then((m) => ({
    default: m.MyApplicationsPage,
  }))
);
const StudentApplicationDetailPage = lazy(() =>
  import("@/features/applications/pages/student/ApplicationDetailPage").then((m) => ({
    default: m.ApplicationDetailPage,
  }))
);

// Lazy load application pages - HR
const HRPendingReviewPage = lazy(() =>
  import("@/features/applications/pages/hr/PendingReviewPage").then((m) => ({
    default: m.PendingReviewPage,
  }))
);
const HRAllApplicationsPage = lazy(() =>
  import("@/features/applications/pages/hr/AllApplicationsPage").then((m) => ({
    default: m.AllApplicationsPage,
  }))
);
const HRApplicationDetailPage = lazy(() =>
  import("@/features/applications/pages/hr/HRApplicationDetailPage").then((m) => ({
    default: m.HRApplicationDetailPage,
  }))
);

// Lazy load application pages - Enterprise
const ForwardedApplicationsPage = lazy(() =>
  import("@/features/applications/pages/enterprise/ForwardedApplicationsPage").then((m) => ({
    default: m.ForwardedApplicationsPage,
  }))
);
const EnterpriseApplicationDetailPage = lazy(() =>
  import("@/features/applications/pages/enterprise/EnterpriseApplicationDetailPage").then((m) => ({
    default: m.EnterpriseApplicationDetailPage,
  }))
);

// Lazy load calendar pages
const StudentCalendarPage = lazy(() =>
  import("@/features/calendar/pages/student/StudentCalendarPage").then((m) => ({
    default: m.StudentCalendarPage,
  }))
);
const HRCalendarPage = lazy(() =>
  import("@/features/calendar/pages/hr/HRCalendarPage").then((m) => ({
    default: m.HRCalendarPage,
  }))
);
const EnterpriseCalendarPage = lazy(() =>
  import("@/features/calendar/pages/enterprise/EnterpriseCalendarPage").then((m) => ({
    default: m.EnterpriseCalendarPage,
  }))
);

// Lazy load chat pages
const StudentMessagesPage = lazy(() =>
  import("@/features/chat/pages/student/StudentMessagesPage").then((m) => ({
    default: m.StudentMessagesPage,
  }))
);
const HRMessagesPage = lazy(() =>
  import("@/features/chat/pages/hr/HRMessagesPage").then((m) => ({
    default: m.HRMessagesPage,
  }))
);
const EnterpriseMessagesPage = lazy(() =>
  import("@/features/chat/pages/enterprise/EnterpriseMessagesPage").then((m) => ({
    default: m.EnterpriseMessagesPage,
  }))
);

// Lazy load notifications pages (all roles)
const NotificationsPage = lazy(() =>
  import("@/features/notifications/pages/NotificationListPage").then((m) => ({
    default: m.NotificationListPage,
  }))
);
const NotificationSettingsPage = lazy(() =>
  import("@/features/notifications/pages/NotificationSettingsPage").then((m) => ({
    default: m.NotificationSettingsPage,
  }))
);
const GeneralSettingsPage = lazy(() =>
  import("@/features/settings/pages/GeneralSettingsPage").then((m) => ({
    default: m.GeneralSettingsPage,
  }))
);

// Lazy load HR dashboard pages
const HRDashboardPageImpl = lazy(() =>
  import("@/features/hr/pages/HRDashboardPage").then((m) => ({
    default: m.HRDashboardPage,
  }))
);
const PipelineKanbanPage = lazy(() =>
  import("@/features/hr/pages/PipelineKanbanPage").then((m) => ({
    default: m.PipelineKanbanPage,
  }))
);
const EnterprisesListPage = lazy(() =>
  import("@/features/hr/pages/EnterprisesListPage").then((m) => ({
    default: m.EnterprisesListPage,
  }))
);
const EnterpriseDetailPage = lazy(() =>
  import("@/features/hr/pages/EnterpriseDetailPage").then((m) => ({
    default: m.EnterpriseDetailPage,
  }))
);
const CreateEnterprisePage = lazy(() =>
  import("@/features/hr/pages/CreateEnterprisePage").then((m) => ({
    default: m.CreateEnterprisePage,
  }))
);
const StudentsListPage = lazy(() =>
  import("@/features/hr/pages/StudentsListPage").then((m) => ({
    default: m.StudentsListPage,
  }))
);
const HRSearchPage = lazy(() =>
  import("@/features/hr/pages/HRSearchPage").then((m) => ({
    default: m.HRSearchPage,
  }))
);
const AuditLogPage = lazy(() =>
  import("@/features/hr/pages/AuditLogPage").then((m) => ({
    default: m.AuditLogPage,
  }))
);
const HRInterestsPage = lazy(() =>
  import("@/features/hr/pages/HRInterestsPage").then((m) => ({
    default: m.HRInterestsPage,
  }))
);

// Lazy load enterprise dashboard pages
const EnterpriseDashboardPageImpl = lazy(() =>
  import("@/features/enterprise-dashboard/pages/EnterpriseDashboardPage").then((m) => ({
    default: m.EnterpriseDashboardPage,
  }))
);
const WatchlistPage = lazy(() =>
  import("@/features/enterprise-dashboard/pages/WatchlistPage").then((m) => ({
    default: m.WatchlistPage,
  }))
);
const EnterprisePipelinePage = lazy(() =>
  import("@/features/enterprise-dashboard/pages/EnterprisePipelinePage").then((m) => ({
    default: m.EnterprisePipelinePage,
  }))
);

// Lazy load admin pages
const AdminDashboardPageImpl = lazy(() =>
  import("@/features/admin/pages/AdminDashboardPage").then((m) => ({
    default: m.AdminDashboardPage,
  }))
);
const AdminUsersPage = lazy(() =>
  import("@/features/admin/pages/UsersPage").then((m) => ({
    default: m.UsersPage,
  }))
);
const AdminUserProfilePage = lazy(() =>
  import("@/features/admin/pages/AdminUserProfilePage").then((m) => ({
    default: m.AdminUserProfilePage,
  }))
);
const AdminRolesPage = lazy(() =>
  import("@/features/admin/pages/RolesPage").then((m) => ({
    default: m.RolesPage,
  }))
);
const AdminRolePermissionsPage = lazy(() =>
  import("@/features/admin/pages/RolePermissionsPage").then((m) => ({
    default: m.RolePermissionsPage,
  }))
);
const AdminFeatureFlagsPage = lazy(() =>
  import("@/features/admin/pages/FeatureFlagsPage").then((m) => ({
    default: m.FeatureFlagsPage,
  }))
);
const AdminSystemConfigPage = lazy(() =>
  import("@/features/admin/pages/SystemConfigPage").then((m) => ({
    default: m.SystemConfigPage,
  }))
);
const AdminSuccessStoriesPage = lazy(() =>
  import("@/features/success-stories/pages/admin/AdminSuccessStoriesPage").then((m) => ({
    default: m.AdminSuccessStoriesPage,
  }))
);
const PublicSuccessStoriesPage = lazy(() =>
  import("@/features/success-stories/pages/public/SuccessStoriesPage").then((m) => ({
    default: m.SuccessStoriesPage,
  }))
);
const PublicSuccessStoryDetailPage = lazy(() =>
  import("@/features/success-stories/pages/public/SuccessStoryDetailPage").then((m) => ({
    default: m.SuccessStoryDetailPage,
  }))
);

// Lazy load legal pages
const TermsPage = lazy(() => import("@/features/legal").then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() =>
  import("@/features/legal").then((m) => ({ default: m.PrivacyPage }))
);

// Page loading fallback
function PageSkeleton() {
  return (
    <div className="flex h-screen items-center justify-center">
      <Loader2 className="text-primary h-8 w-8 animate-spin" />
    </div>
  );
}

// 404 page
function NotFoundPage() {
  return (
    <div className="bg-muted/50 flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-foreground text-6xl font-bold">404</h1>
        <p className="text-muted-foreground mt-4 text-xl">Page not found</p>
        <Button asChild className="mt-8">
          <Link to="/">Go Home</Link>
        </Button>
      </div>
    </div>
  );
}

// Unauthorized page
function UnauthorizedPage() {
  return (
    <div className="bg-muted/50 flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-foreground text-4xl font-bold">Access Denied</h1>
        <p className="text-muted-foreground mt-4">
          You don&apos;t have permission to access this page.
        </p>
        <Button asChild className="mt-8">
          <Link to="/">Go Home</Link>
        </Button>
      </div>
    </div>
  );
}

/**
 * App routes - exported for testing without router wrapper
 */
export function AppRoutes() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>
        <Routes>
          {/* Public routes */}
          {/* Landing page: guests see it; authenticated users are sent to their dashboard. */}
          <Route
            path="/"
            element={
              <GuestRoute>
                <LandingPage />
              </GuestRoute>
            }
          />
          <Route
            path="/openings"
            element={
              <PublicAppLayout>
                <OpeningsListPage />
              </PublicAppLayout>
            }
          />
          <Route
            path="/openings/:id"
            element={
              <PublicAppLayout>
                <OpeningDetailPage />
              </PublicAppLayout>
            }
          />
          <Route
            path="/success-stories"
            element={
              <PublicAppLayout>
                <PublicSuccessStoriesPage />
              </PublicAppLayout>
            }
          />
          <Route
            path="/success-stories/:id"
            element={
              <PublicAppLayout>
                <PublicSuccessStoryDetailPage />
              </PublicAppLayout>
            }
          />
          <Route
            path="/terms"
            element={
              <PublicAppLayout>
                <TermsPage />
              </PublicAppLayout>
            }
          />
          <Route
            path="/privacy"
            element={
              <PublicAppLayout>
                <PrivacyPage />
              </PublicAppLayout>
            }
          />

          {/* Auth routes (guest only) */}
          <Route
            path="/login"
            element={
              <GuestRoute>
                <LoginPage />
              </GuestRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <GuestRoute>
                <EnterpriseSignupPage />
              </GuestRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<OAuthCallbackPage />} />
          <Route path="/pending-approval" element={<PendingApprovalPage />} />

          {/* Protected routes with unified layout */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Outlet />
                </AppLayout>
              </ProtectedRoute>
            }
          >
            {/* Notifications (all authenticated roles) */}
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/notifications/settings" element={<NotificationSettingsPage />} />

            {/* Settings (all authenticated roles) — General Settings page with tabs for Security and Notifications */}
            <Route path="/settings" element={<GeneralSettingsPage />} />

            {/* Student Dashboard & Profile */}
            <Route
              path="/dashboard"
              element={
                <AuthGuard allowedRoles={["STUDENT"]}>
                  <DashboardPage />
                </AuthGuard>
              }
            />
            <Route
              path="/profile"
              element={
                <AuthGuard allowedRoles={["STUDENT"]}>
                  <ProfilePage />
                </AuthGuard>
              }
            />

            {/* Student Applications */}
            <Route
              path="/applications"
              element={
                <AuthGuard allowedRoles={["STUDENT"]}>
                  <MyApplicationsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/applications/:id"
              element={
                <AuthGuard allowedRoles={["STUDENT"]}>
                  <StudentApplicationDetailPage />
                </AuthGuard>
              }
            />

            {/* Messages (per role) */}
            <Route
              path="/messages"
              element={
                <AuthGuard allowedRoles={["STUDENT"]}>
                  <StudentMessagesPage />
                </AuthGuard>
              }
            />
            <Route
              path="/hr/messages"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <HRMessagesPage />
                </AuthGuard>
              }
            />
            <Route
              path="/enterprise/messages"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <EnterpriseMessagesPage />
                </AuthGuard>
              }
            />

            {/* Student Calendar */}
            <Route
              path="/calendar"
              element={
                <AuthGuard allowedRoles={["STUDENT"]}>
                  <StudentCalendarPage />
                </AuthGuard>
              }
            />

            {/* HR Calendar */}
            <Route
              path="/hr/calendar"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <HRCalendarPage />
                </AuthGuard>
              }
            />

            {/* Enterprise Calendar (read-only) */}
            <Route
              path="/enterprise/calendar"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <EnterpriseCalendarPage />
                </AuthGuard>
              }
            />

            {/* Talent Search (Enterprise/HR) */}
            <Route
              path="/talent"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER", "HR_STAFF", "ADMIN"]}>
                  <TalentSearchPage />
                </AuthGuard>
              }
            />
            <Route
              path="/talent/:profileId"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER", "HR_STAFF", "ADMIN"]}>
                  <TalentProfilePage />
                </AuthGuard>
              }
            />

            {/* Enterprise Profile */}
            <Route
              path="/enterprise/profile"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <EnterpriseProfilePage />
                </AuthGuard>
              }
            />

            {/* Admin Profile */}
            <Route
              path="/admin/profile"
              element={
                <AuthGuard allowedRoles={["ADMIN"]}>
                  <AdminProfilePage />
                </AuthGuard>
              }
            />

            {/* HR Profile */}
            <Route
              path="/hr/profile"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <HRProfilePage />
                </AuthGuard>
              }
            />

            {/* Enterprise Job Routes */}
            <Route
              path="/enterprise/jobs"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <EnterpriseJobsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/enterprise/jobs/create"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <CreateJobPage />
                </AuthGuard>
              }
            />
            <Route
              path="/enterprise/jobs/:jobId"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <EnterpriseJobDetailPage />
                </AuthGuard>
              }
            />
            <Route
              path="/enterprise/jobs/:jobId/edit"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <EditJobPage />
                </AuthGuard>
              }
            />

            {/* HR Job Approval Routes */}
            <Route
              path="/hr/job-approvals"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <HRJobReviewPage />
                </AuthGuard>
              }
            />
            <Route
              path="/hr/jobs/:jobId"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <HRJobDetailPage />
                </AuthGuard>
              }
            />

            {/* HR Dashboard & Pipeline */}
            <Route
              path="/hr/dashboard"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <HRDashboardPageImpl />
                </AuthGuard>
              }
            />
            <Route
              path="/hr/pipeline"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <PipelineKanbanPage />
                </AuthGuard>
              }
            />

            {/* HR Enterprises Routes */}
            <Route
              path="/hr/enterprises"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <EnterprisesListPage />
                </AuthGuard>
              }
            />
            <Route
              path="/hr/enterprises/create"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <CreateEnterprisePage />
                </AuthGuard>
              }
            />
            <Route
              path="/hr/enterprises/:id"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <EnterpriseDetailPage />
                </AuthGuard>
              }
            />

            {/* HR Students Route */}
            <Route
              path="/hr/students"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <StudentsListPage />
                </AuthGuard>
              }
            />
            <Route
              path="/hr/students/:profileId"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <TalentProfilePage />
                </AuthGuard>
              }
            />

            {/* HR Unified Search Route */}
            <Route
              path="/hr/search"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <HRSearchPage />
                </AuthGuard>
              }
            />

            {/* Audit Log Route — Admin only (backend AUDIT_VIEW is ADMIN-only) */}
            <Route
              path="/hr/audit-log"
              element={
                <AuthGuard allowedRoles={["ADMIN"]}>
                  <AuditLogPage />
                </AuthGuard>
              }
            />

            {/* HR Enterprise Interests Route */}
            <Route
              path="/hr/interests"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <HRInterestsPage />
                </AuthGuard>
              }
            />

            {/* HR Application Routes */}
            <Route
              path="/hr/applications/pending"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <HRPendingReviewPage />
                </AuthGuard>
              }
            />
            <Route
              path="/hr/applications"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <HRAllApplicationsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/hr/applications/:id"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <HRApplicationDetailPage />
                </AuthGuard>
              }
            />

            {/* Enterprise Dashboard */}
            <Route
              path="/enterprise/dashboard"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <EnterpriseDashboardPageImpl />
                </AuthGuard>
              }
            />

            {/* Enterprise Watchlist */}
            <Route
              path="/enterprise/watchlist"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <WatchlistPage />
                </AuthGuard>
              }
            />

            {/* Enterprise Pipeline */}
            <Route
              path="/enterprise/pipeline"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <EnterprisePipelinePage />
                </AuthGuard>
              }
            />

            {/* Enterprise Application Routes */}
            <Route
              path="/enterprise/jobs/:openingId/applications"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <ForwardedApplicationsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/enterprise/applications/:id"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <EnterpriseApplicationDetailPage />
                </AuthGuard>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <AuthGuard allowedRoles={["ADMIN"]}>
                  <AdminDashboardPageImpl />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AuthGuard allowedRoles={["ADMIN"]}>
                  <AdminUsersPage />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/users/:userId/profile"
              element={
                <AuthGuard allowedRoles={["ADMIN"]}>
                  <AdminUserProfilePage />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/roles"
              element={
                <AuthGuard allowedRoles={["ADMIN"]}>
                  <AdminRolesPage />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/roles/:roleId/permissions"
              element={
                <AuthGuard allowedRoles={["ADMIN"]}>
                  <AdminRolePermissionsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/feature-flags"
              element={
                <AuthGuard allowedRoles={["ADMIN"]}>
                  <AdminFeatureFlagsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/system-config"
              element={
                <AuthGuard allowedRoles={["ADMIN"]}>
                  <AdminSystemConfigPage />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/success-stories"
              element={
                <AuthGuard allowedRoles={["ADMIN"]}>
                  <AdminSuccessStoriesPage />
                </AuthGuard>
              }
            />

            {/* Fallback dashboard routes */}
            <Route
              path="/hr/*"
              element={
                <AuthGuard allowedRoles={["HR_STAFF", "ADMIN"]}>
                  <HRDashboardPageImpl />
                </AuthGuard>
              }
            />
            <Route
              path="/enterprise/*"
              element={
                <AuthGuard allowedRoles={["ENTERPRISE_RECRUITER"]}>
                  <EnterpriseDashboardPageImpl />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/*"
              element={
                <AuthGuard allowedRoles={["ADMIN"]}>
                  <AdminDashboardPageImpl />
                </AuthGuard>
              }
            />
          </Route>

          {/* Preview routes - only available in development (with layout) */}
          {import.meta.env.DEV && (
            <Route
              element={
                <AppLayout>
                  <Outlet />
                </AppLayout>
              }
            >
              <Route path="/preview/dashboard" element={<DashboardPage />} />
              <Route path="/preview/profile" element={<ProfilePage />} />
              <Route path="/preview/talent" element={<TalentSearchPage />} />
              <Route path="/preview/talent/:profileId" element={<TalentProfilePage />} />
            </Route>
          )}

          {/* Error pages */}
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

/**
 * Root App component with all providers
 */
export default function App() {
  return (
    <QueryProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster />
    </QueryProvider>
  );
}
